import React, { useEffect, useState } from "react";

import { Card } from "@mui/material";
import { NeutralColor } from "node-library/dist/colors";
import { Severity } from "node-library/dist/constants";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";
import { SampleSortColumn, SortOrder } from "node-library/dist/paginations";
import {
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";
import {
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendSample,
	FrontendUserInfo,
} from "node-library/dist/portal";
import {
	GreenButton,
	KebabDropdownButton,
} from "../../../common-components/button";
import {
	CustomSearchBar,
	RowDropdown,
} from "../../../common-components/inputs";
import {
	EditIcon,
	LicenseIcon,
	SortArrowIcon,
} from "../../../common-components/svgIcons";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import {
	getAdditionalInfoValueDisplayText,
	getSampleSortString,
} from "../../../helpers/sampleHelper";
import { sendPOSTRequest } from "../../../requestHelper/sendRequest";
import EditSampleModal from "./modal/editSampleModal";
import UploadSampleImportFileDrawer from "./modal/uploadSampleImportFileDrawer";

interface Props {
	selectedTestCategory: FrontendCompanyTestCategory;
	organizationSamples: FrontendSample[];
	setOrganizationSamples: React.Dispatch<
		React.SetStateAction<FrontendSample[]>
	>;
	reloadOrganizationDetailedInfo: () => void;
	organizationId: string;
	user: FrontendUserInfo;
}

export default function OrgSamplesSection(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	//license info
	const {
		organizationId,
		organizationSamples,
		setOrganizationSamples,
		reloadOrganizationDetailedInfo,
	} = props;

	const [samplesToDisplay, setSamplesToDisplay] =
		useState<FrontendSample[]>(organizationSamples);

	// const [newLicenseModalOpen, setNewLicenseModalOpen] =
	// 	useState<boolean>(false);

	const [loading, setLoading] = useState<boolean>(false);

	const [sortBy, setSortBy] = useState<SampleSortColumn>(
		SampleSortColumn.FULL_SAMPLE_ID,
	);
	const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);

	const [sampleSearchText, setSampleSearchText] = useState<string>("");

	const [editingSample, setEditingSample] = useState<
		FrontendSample | undefined
	>();

	const [editingSampleModalOpen, setEditingSampleModalOpen] =
		useState<boolean>(false);
	const [importSampleModalOpen, setImportSampleModalOpen] =
		useState<boolean>(false);

	const doesUserHaveEditAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.samples.updateInfo,
			)
		: false;

	function toggleSortOrder() {
		if (sortOrder === SortOrder.ASC) {
			setSortOrder(SortOrder.DESC);
		} else {
			setSortOrder(SortOrder.ASC);
		}
	}

	async function editSample(updatedSample: FrontendSample) {
		setLoading(true);
		try {
			const response = await sendPOSTRequest("/samples/update-sample", {
				organizationId,
				sampleId: updatedSample.sampleId,
				sampleName: updatedSample.sampleName.trim(),
				lotCode: updatedSample.lotCode.trim(),
				sampleDescription: updatedSample.sampleDescription.trim(),
				matrix: updatedSample.matrix,
				matrixId: updatedSample.matrixId,
				isCustomMatrix: updatedSample.isCustomMatrix,
				frontendAdditionalInfo: updatedSample.sampleAdditionalInfos,
			});
			setOrganizationSamples((prevSamples) =>
				prevSamples.map((sample) =>
					sample.sampleId === updatedSample.sampleId ? updatedSample : sample,
				),
			);
			setEditingSampleModalOpen(false);

			openSnackbar(
				translation("SampleUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
		} catch (error) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		if (props.organizationSamples.length > 0) {
			let samples: Array<FrontendSample> = [...props.organizationSamples];
			if (sampleSearchText) {
				samples = samples.filter((sample) => {
					let matrixText: string = "";
					if (sample.isCustomMatrix) {
						matrixText = sample.matrix;
					} else if (sample.matrixId) {
						const matchingMatrix: FrontendCompanySampleMatrix | undefined =
							props.selectedTestCategory.sampleMatrices.find((matrix) => {
								return matrix.matrixId === sample.matrixId;
							});

						if (matchingMatrix) {
							matrixText = `${matchingMatrix.subMatrix}`;
						}
					}
					return (
						sample.fullSampleId?.includes(sampleSearchText) ||
						sample.lotCode.includes(sampleSearchText) ||
						sample.sampleName.includes(sampleSearchText) ||
						sample.sampleDescription.includes(sampleSearchText) ||
						matrixText.includes(sampleSearchText)
					);
				});
			}

			const sortedSamples = samples.sort((a, b) => {
				const valueA = a[sortBy] ?? ""; // Provide a default value for undefined
				const valueB = b[sortBy] ?? ""; // Provide a default value for undefined

				if (sortOrder === SortOrder.ASC) {
					return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
				} else {
					return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
				}
			});

			setSamplesToDisplay(sortedSamples);
		} else {
			setSamplesToDisplay([]);
		}
	}, [sortBy, sortOrder, sampleSearchText, props.organizationSamples]); // Dependency array includes sortBy, sortOrder, and organizationSamples

	// const addLicense = (newLicense: FrontendOrganizationLicense) => {
	// 	setOrganizationSamples((prevLicenses) => [
	// 		...prevLicenses, // Spread the previous licenses
	// 		newLicense, // Add the new license at the end
	// 	]);
	// };

	const sortOptionSelection: React.ReactNode = (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "5px",
				marginLeft: "auto",
			}}
		>
			<div
				onClick={() => {
					toggleSortOrder();
				}}
				style={{ cursor: "pointer" }}
			>
				<SortArrowIcon sortOrder={sortOrder} />
			</div>
			<RowDropdown
				id="order-sort-order-dropdown"
				className="sort-order-dropdown"
				label={"a"}
				labelClassName="hide"
				noBorder
				options={Object.values(SampleSortColumn).map((value) => {
					return {
						value: value,
						title: getSampleSortString(value, translation),
					};
				})}
				value={sortBy}
				setValue={setSortBy}
			></RowDropdown>
		</div>
	);

	const searchBar: React.ReactNode = (
		<CustomSearchBar
			id="view-order-page-searchbar"
			searchText={sampleSearchText}
			setSearchText={setSampleSearchText}
			placeholder={translation("Search")}
			searchButtonText={translation("Search")}
			onSearch={() => {}}
		></CustomSearchBar>
	);

	const importSampleButton: React.ReactNode = (
		<GreenButton
			id="import-sample-button"
			onClick={() => {
				setImportSampleModalOpen(true);
			}}
			disabled={!doesUserHaveEditAccess}
		>
			<AddIcon></AddIcon>
			{translation("Import")}
		</GreenButton>
	);

	const sampleOptionCards: Array<React.ReactNode> = samplesToDisplay.map(
		(sample, index) => {
			let matrixText: string = "";
			if (sample.isCustomMatrix) {
				matrixText = sample.matrix;
			} else if (sample.matrixId) {
				const matchingMatrix: FrontendCompanySampleMatrix | undefined =
					props.selectedTestCategory.sampleMatrices.find((matrix) => {
						return matrix.matrixId === sample.matrixId;
					});

				if (matchingMatrix) {
					matrixText = `${matchingMatrix.subMatrix}`;
				}
			}
			const actionOptions = [
				{
					key: `edit-sample-${sample.sampleId}`,
					onClick: () => {
						setEditingSample(sample);
						setEditingSampleModalOpen(true);
					},
					children: (
						<div className="icon-dropdown-menu-item">
							<EditIcon strokeColor={NeutralColor.NEUTRAL_GREY_950} />
							{translation("Edit")}
						</div>
					),
				},
			];
			const sampleHeader: React.ReactNode = (
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						gap: "5px",
					}}
				>
					<div></div>

					{doesUserHaveEditAccess && (
						<KebabDropdownButton
							menuOptions={actionOptions}
							id={`sample-more-action-menu-${sample.sampleId}`}
							onClick={(e) => {}}
						></KebabDropdownButton>
					)}
				</div>
			);
			const sampleBody: React.ReactNode = (
				<div
					style={{
						marginTop: "10px",
						display: "flex",
						flexDirection: "column",
						gap: "5px",
					}}
				>
					<div className="order-cell-body-row typography-body-02">
						<div className="sample-info-row-description">
							{translation("SampleId")}
						</div>
						<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
							{sample.fullSampleId}
						</div>
					</div>
					<div className="order-cell-body-row typography-body-02">
						<div className="sample-info-row-description">
							{translation("LotCode")}
						</div>
						<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
							{sample.lotCode}
						</div>
					</div>
					<div className="order-cell-body-row typography-body-02">
						<div className="sample-info-row-description">
							{translation("SampleName")}
						</div>
						<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
							{sample.sampleName}
						</div>
					</div>
					{sample.sampleDescription && (
						<div className="order-cell-body-row typography-body-02">
							<div className="sample-info-row-description">
								{translation("SampleDescription")}
							</div>
							<div
								className="overflow-ellipsis-text"
								style={{ maxWidth: "50%" }}
							>
								{sample.sampleDescription}
							</div>
						</div>
					)}

					<div className="order-cell-body-row typography-body-02">
						<div className="sample-info-row-description">
							{translation("Matrix")}
						</div>
						<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
							{matrixText}
						</div>
					</div>

					{sample.sampleAdditionalInfos.map((info) => {
						return (
							<div className="order-cell-body-row typography-body-02">
								<div className="sample-info-row-description">{`${info.additionalFieldName} `}</div>
								<div className="overflow-ellipsis-text">
									{getAdditionalInfoValueDisplayText(info)}
								</div>
							</div>
						);
					})}

					<div className="order-cell-body-row typography-body-02">
						<div className="sample-info-row-description">
							{translation("Createdon")}
						</div>
						<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
							{formatCustomDate(sample.inserted as Date)}
						</div>
					</div>
				</div>
			);
			return (
				<Card
					className="card-border-radius-14p card-medium-padding "
					key={sample.sampleId}
					sx={{ backgroundColor: "#F3F3F5" }}
				>
					{sampleHeader}
					{sampleBody}
				</Card>
			);
		},
	);

	const sampleInformation: React.ReactNode = (
		<Card className="common-card card-normal-padding">
			<div>
				<div className="org-sample-card-header">
					<div className="profile-information-container">
						<div className="profile-icon-header-row typography-heading-03">
							<LicenseIcon></LicenseIcon>
							{translation("SampleInformation")}
						</div>
						<div
							className="typography-caption"
							style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
						>
							{translation("SampleInformationDescription")}
						</div>
					</div>
					{sortOptionSelection}
				</div>
				<div className="org-sample-card-header">
					{searchBar}
					{doesUserHaveEditAccess ? importSampleButton : <></>}
				</div>

				<div className="profile-license-card-grid">{sampleOptionCards}</div>
			</div>
		</Card>
	);

	return (
		<>
			{sampleInformation}
			{editingSample && (
				<EditSampleModal
					selectedTestCategory={props.selectedTestCategory}
					editSample={editSample}
					open={editingSampleModalOpen}
					setOpen={setEditingSampleModalOpen}
					sample={editingSample as FrontendSample}
					loading={loading}
					setLoading={setLoading}
				></EditSampleModal>
			)}
			<UploadSampleImportFileDrawer
				open={importSampleModalOpen}
				setOpen={setImportSampleModalOpen}
				organizationId={organizationId}
				selectedTestCategory={props.selectedTestCategory}
				setOrganizationSamples={setOrganizationSamples}
				reloadOrganizationDetailedInfo={reloadOrganizationDetailedInfo}
			></UploadSampleImportFileDrawer>
		</>
	);
}
